/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { BibleBookId, BibleLanguage, BibleManifest } from "@packages/sdk";
import { ScrollArea, spacing, useDynamicViewport } from "@packages/ui";
import { useParams } from "next/navigation";
import type {
  CSSProperties,
  Dispatch,
  MouseEvent,
  SetStateAction,
} from "react";
import { useCallback, useContext, useMemo } from "react";

import { BibleLanguageContext } from "../BibleLanguageProvider";
import {
  BIBLE_DESKTOP_WIDTH,
  isCustomDesktop,
  isLargeDevice,
  isMediumDevice,
} from "../bibleStyle.constants";
import { BibleNavBookSection } from "./BibleNavBookSection";
import type { RtlOrderFn } from "./BibleNavigation";

const booksStyle: Interpolation = {
  alignItems: "center",
  display: "grid",
  gridAutoRows: "minmax(56px, min-content)",
  gridTemplateColumns: "1fr",
  [isCustomDesktop]: { gridTemplateColumns: "repeat(2, 1fr)" },
  [isLargeDevice]: { gridTemplateColumns: "repeat(2, 1fr)" },
  [isMediumDevice]: { gridTemplateColumns: "repeat(2, 1fr)" },
};
const bookItemStyle: Interpolation = {
  minWidth: "160px",
};
const bookSectionHeaderStyle: Interpolation = {
  display: "flex",
  gridColumn: "initial",
  paddingLeft: spacing.m,
  placeItems: "center",
  [isCustomDesktop]: {
    display: "revert",
    gridColumn: "span 2",
    paddingLeft: "revert",
    placeItems: "revert",
  },
  [isLargeDevice]: { gridColumn: "span 2" },
  [isMediumDevice]: { gridColumn: "span 2" },
};
const rtlItemStyle: Interpolation = {
  flexDirection: "row-reverse",
};

type BookNavigatorProps = {
  language: BibleLanguage;
  manifest: BibleManifest;
  navBook: BibleBookId;
  rtlOrder: RtlOrderFn;
  setNavBook: Dispatch<SetStateAction<BibleBookId>>;
  className?: string;
};

export const BookNavigator = ({
  language,
  className = "",
  manifest,
  rtlOrder,
  navBook,
  setNavBook,
}: BookNavigatorProps) => {
  const t = useTranslations();
  const { rtl } = useContext(BibleLanguageContext);
  const analytics = useAnalytics();
  const { translation } = useParams();
  const oldTestamentBooks = useMemo(() => {
    return manifest.books.filter((b) => b.testament === "old");
  }, [manifest]);
  const newTestamentBooks = useMemo(() => {
    return manifest.books.filter((b) => b.testament === "new");
  }, [manifest]);
  const isDesktop = useDynamicViewport({ minimumWidth: BIBLE_DESKTOP_WIDTH });

  const itemStyle = useCallback(
    (type: "old" | "new", idx: number): CSSProperties => {
      if (rtl) {
        if (type === "old") return { order: rtlOrder(idx + 1, 2) };
        return { order: rtlOrder(idx + oldTestamentBooks.length + 2, 2) };
      }

      return undefined;
    },
    [rtl, oldTestamentBooks],
  );

  const gotoBook = useCallback(
    (bname: BibleBookId, e: Event | MouseEvent) => {
      analytics.track({
        event: HallowAnalyticsEvent.TappedBibleBook,
        properties: {
          bible_language: language,
          bible_translation: translation,
          starting_book: navBook,
          new_book: bname,
        },
      });
      if (!isDesktop) {
        analytics.track({
          event: HallowAnalyticsEvent.OpenedBibleChapterMenu,
        });
      }
      e?.preventDefault();
      setNavBook(bname);
    },
    [navBook, language, translation, isDesktop],
  );

  return (
    <ScrollArea viewportProps={{ className }}>
      <div css={booksStyle}>
        <BibleNavBookSection
          headerCSS={bookSectionHeaderStyle}
          books={oldTestamentBooks}
          itemCSS={[bookItemStyle, rtl ? rtlItemStyle : null]}
          itemStyle={itemStyle.bind(null, "old")}
          title={t("web_bible_navigation_old_testament_section_title")}
          navBook={navBook}
          onItemClick={gotoBook}
        />
        <BibleNavBookSection
          headerCSS={bookSectionHeaderStyle}
          books={newTestamentBooks}
          itemCSS={[bookItemStyle, rtl ? rtlItemStyle : null]}
          itemStyle={itemStyle.bind(null, "new")}
          title={t("web_bible_navigation_new_testament_section_title")}
          titlePosition={rtl ? oldTestamentBooks.length + 2 : undefined}
          navBook={navBook}
          onItemClick={gotoBook}
        />
      </div>
    </ScrollArea>
  );
};
